import React, { useEffect, useState } from "react";
import TableContainer from "../../Components/TableContainer/TableContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router";
import {
  loader,
  searchDataWithMultipleKeys,
  paginate,
  snackbar,
} from "../../utils";
import {
  getAllIptvChannel,
  deleteIptvChannel,
} from "../../Components/service/admin";
import { DeleteButton } from "../../Components/Buttons/DeleteButton";
import { AddIptvChannel } from "./AddIptvChannel";
import { baseUrl } from "../../Components/service/Api";

export default function IptvChannelList() {
  const [search, setSearch] = useState("");
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const navigate = useNavigate();

  // Fetch all IPTV channels
  async function getAllIptvChannels() {
    try {
      loader.start();
      let res = await getAllIptvChannel();

      const response = res?.data;
      if (response?.status) {
        setAllData(response?.data || []);
        setFilteredData(response?.data || []);
        snackbar.success(
          response?.message || "IPTV Channels fetched successfully"
        );
      } else {
        snackbar.error(response?.message || "Failed to fetch channels");
      }
    } catch (error) {
      console.log(error);
      snackbar.error("Failed to fetch IPTV channels");
    } finally {
      loader.stop();
    }
  }

  useEffect(() => {
    getAllIptvChannels();
  }, []);

  // Handle search
  useEffect(() => {
    if (!search.trim()) {
      setFilteredData(allData);
    } else {
      const searchedData = allData.filter((channel) => {
        const searchTerm = search.toLowerCase().trim();
        return (
          channel?.name?.toLowerCase().includes(searchTerm) ||
          channel?.channelCategory?.name?.toLowerCase().includes(searchTerm) ||
          channel?.lcnNo?.toString().includes(searchTerm)
        );
      });
      setFilteredData(searchedData);
    }
    setPage(0);
  }, [search, allData]);

  // Delete handler
  async function deleteChannel(data) {
    try {
      loader.start();
      await deleteIptvChannel(data._id);
      snackbar.success("IPTV Channel deleted successfully");
      setAllData((prevData) =>
        prevData.filter((item) => item._id !== data._id)
      );
      setFilteredData((prevData) =>
        prevData.filter((item) => item._id !== data._id)
      );
    } catch (error) {
      console.log(error);
      snackbar.error("Failed to delete channel");
    } finally {
      loader.stop();
    }
  }

  // Paginate the filtered data
  const paginatedData = paginate(filteredData, page, rowsPerPage);

  return (
    <>
      <h2 className="fw-600">IPTV Channels</h2>
      <div className="row mt-4">
        <div className="col-lg-5 col-md-6 col-sm-12 col-12">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search channels by name, category, LCN number..."
            className="form-control"
          />
        </div>
        <div className="col w-100 d-flex justify-content-end">
          <AddIptvChannel onSubmit={getAllIptvChannels} />
        </div>
      </div>

      <div className="mt-4">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Logo</TableCell>
                <TableCell>Channel Name</TableCell>
                <TableCell>Category</TableCell>
                {/* <TableCell> language </TableCell> */}
                <TableCell>LCN No</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData?.map((channel, index) => (
                <TableRow hover key={channel._id || index}>
                  {/* Logo */}
                  <TableCell>
                    {channel?.image ? (
                      <Avatar
                        src={`${baseUrl}/${channel.image}`}
                        alt={channel.name}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "8px",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: "#f0f0f0",
                          borderRadius: "8px",
                        }}
                      >
                        {channel?.name?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                    )}
                  </TableCell>

                  {/* Channel Name */}
                  <TableCell>
                    <div className="fw-bold">{channel?.name || "-"}</div>
                  </TableCell>

                  {/* Category */}
                  <TableCell>{channel?.channelCategory?.name || "-"}</TableCell>
                  {/* language */}
                  {/* <TableCell>{channel?.langage?.name || "-"}</TableCell> */}

                  {/* LCN Number */}
                  <TableCell>
                    <Chip
                      label={channel?.lcnNo || "-"}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>

                  {/* Priority */}
                  <TableCell>
                    <Chip
                      label={channel?.priority || 0}
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <div className="d-flex gap-2">
                      <AddIptvChannel
                        formData={channel}
                        mode="edit"
                        onSubmit={getAllIptvChannels}
                      />
                      <DeleteButton
                        confirmation
                        display={"IPTV Channel"}
                        data={channel}
                        onDelete={deleteChannel}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}

              {paginatedData?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    <div className="text-muted">
                      {search
                        ? "No channels found matching your search"
                        : "No IPTV channels available"}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Table Pagination */}
          <TablePagination
            component="div"
            count={Number(filteredData.length)}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
          />
        </TableContainer>
      </div>
    </>
  );
}
